import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import TerminalAnimation from '../components/features/TerminalAnimation';
import MethodologyPipeline from '../components/features/MethodologyPipeline';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLanguage } from '../contexts/LanguageContext';
import { langToSlug } from '../hooks/useLocalePath';
import { renderT } from '../i18n/renderT';

/* -- Video cover -- exact port of video-cover.js -- */
function useVideoCover(iframeRef: React.RefObject<HTMLIFrameElement | null>) {
    useEffect(() => {
        const RATIO = 16 / 9;
        function resize() {
            const el = iframeRef.current;
            if (!el) return;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            let w: number, h: number;
            if (vw / vh > RATIO) { w = vw; h = vw / RATIO; }
            else { h = vh; w = vh * RATIO; }
            w = Math.ceil(w) + 2; h = Math.ceil(h) + 2;
            el.style.width = `${w}px`;
            el.style.height = `${h}px`;
            el.style.top = `${Math.round((vh - h) / 2)}px`;
            el.style.left = `${Math.round((vw - w) / 2)}px`;
            el.style.transform = 'none';
        }
        resize();
        window.addEventListener('resize', resize);
        return () => window.removeEventListener('resize', resize);
    }, [iframeRef]);
}

/* -- Partner logos -- same 8 logos duplicated for seamless loop -- */
const PARTNER_LOGOS = [
    { name: 'Google Cloud', src: '/img/logos/google-cloud.svg' },
    { name: 'AWS', src: '/img/logos/aws.svg' },
    { name: 'Databricks', src: '/img/logos/databricks.svg' },
    { name: 'Snowflake', src: '/img/logos/snowflake.svg' },
    { name: 'Microsoft', src: '/img/logos/microsoft.svg' },
    { name: 'OpenAI', src: '/img/logos/openai.svg' },
    { name: 'Anthropic', src: '/img/logos/anthropic.svg' },
    { name: 'Google Gemini', src: '/img/logos/google-gemini.svg' },
];

const Home: React.FC = () => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    useVideoCover(iframeRef);
    useScrollReveal();
    const { language, t } = useLanguage();
    const lp = (path: string) => `/${langToSlug(language)}${path}`;

    return (
        <main>
            <SEOHead pageId="home" />
            {/* ============================== VIDEO BACKGROUND ============================== */}
            <div className="video-bg">
                <iframe
                    ref={iframeRef}
                    src="https://player.cloudinary.com/embed/?cloud_name=dklf3lssu&public_id=gemini_generated_video_C2BF10B3_wndj7w&autoplay=true&loop=true&muted=true&controls=false&showLogo=false&showInfo=false&fluid=true&aspectRatio=16:9"
                    allow="autoplay; fullscreen; encrypted-media"
                    frameBorder="0"
                    scrolling="no"
                    loading="lazy"
                    width="1920"
                    height="1080"
                    title="Hero background video"
                />
            </div>

            {/* ============================== HERO ============================== */}
            <section className="hero">
                <div className="hero-inner">
                    {/* Copy */}
                    <div className="hero-copy">
                        <div className="eyebrow">{t('home.eyebrow')}</div>

                        <h1>{renderT(t('home.headline'))}</h1>

                        <p className="hero-sub">
                            {renderT(t('home.heroSub'))}
                        </p>

                        <div className="pills">
                            <span className="pill">{t('home.pill1')}</span>
                            <span className="pill">{t('home.pill2')}</span>
                            <span className="pill">{t('home.pill3')}</span>
                            <span className="pill">{t('home.pill4')}</span>
                        </div>

                        <div className="ctas">
                            <Link to={lp('/contact#contact-form')} className="btn-primary">
                                {t('cta.startDiscovery')}
                                <svg viewBox="0 0 16 16" fill="none">
                                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Link>
                            <Link to={lp('/manifesto')} className="btn-secondary">{t('cta.readManifesto')}</Link>
                        </div>
                    </div>

                    {/* Terminal -- bleeding right */}
                    <div className="hero-terminal">
                        <TerminalAnimation />
                    </div>
                </div>
            </section>

            {/* ============================== CONTENT SECTIONS ============================== */}
            <div className="content-sections">

                {/* ========== LOGO STRIP ========== */}
                <section className="logo-strip" id="partners">
                    <div className="logo-strip-text container">
                        <div className="logo-strip-eyebrow">{t('home.partnersEyebrow')}</div>
                        <p className="logo-strip-headline">
                            {renderT(t('home.partnersHeadline'))}
                        </p>
                    </div>

                    <div className="logo-scroll-outer">
                        <div className="logo-scroll-wrap">
                            <div className="logo-track">
                                {/* SET 1 */}
                                {PARTNER_LOGOS.map(logo => (
                                    <div key={`s1-${logo.name}`} className="logo-item">
                                        <img className="li-mark" src={logo.src} alt={logo.name} width="24" height="24" />
                                        <span className="li-name">{logo.name}</span>
                                    </div>
                                ))}
                                {/* SET 2 -- seamless loop duplicate */}
                                {PARTNER_LOGOS.map(logo => (
                                    <div key={`s2-${logo.name}`} className="logo-item">
                                        <img className="li-mark" src={logo.src} alt={logo.name} width="24" height="24" />
                                        <span className="li-name">{logo.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ========== THE PROBLEM WE SOLVE ========== */}
                <section className="section" id="problem">
                    <div className="container">
                        <div className="section-label reveal">{t('home.problemLabel')}</div>

                        <h2 className="section-headline reveal">
                            {renderT(t('home.problemHeadline'))}
                        </h2>

                        <p className="section-sub reveal">
                            {t('home.problemSub')}
                        </p>

                        <div className="grid-2">
                            {/* Card 1 -- Fragmented Data */}
                            <div className="card card-accent reveal reveal-d1">
                                <div className="problem-icon">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M4 6h16M4 12h16M4 18h16" />
                                        <path d="M12 6v12" opacity=".35" />
                                        <line x1="8" y1="9" x2="8" y2="9.01" strokeWidth="3" />
                                        <line x1="16" y1="15" x2="16" y2="15.01" strokeWidth="3" />
                                    </svg>
                                </div>
                                <div className="card-title">{t('home.card1Title')}</div>
                                <p className="card-body">{t('home.card1Body')}</p>
                            </div>

                            {/* Card 2 -- Pilots That Never Scale */}
                            <div className="card card-accent reveal reveal-d2">
                                <div className="problem-icon">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                        <circle cx="12" cy="12" r="3" opacity=".35" />
                                    </svg>
                                </div>
                                <div className="card-title">{t('home.card2Title')}</div>
                                <p className="card-body">{t('home.card2Body')}</p>
                            </div>

                            {/* Card 3 -- No Economic Thesis */}
                            <div className="card card-accent reveal reveal-d3">
                                <div className="problem-icon">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                                    </svg>
                                </div>
                                <div className="card-title">{t('home.card3Title')}</div>
                                <p className="card-body">{t('home.card3Body')}</p>
                            </div>

                            {/* Card 4 -- Capability & Adoption Gaps */}
                            <div className="card card-accent reveal reveal-d4">
                                <div className="problem-icon">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                        <circle cx="9" cy="7" r="4" />
                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                                    </svg>
                                </div>
                                <div className="card-title">{t('home.card4Title')}</div>
                                <p className="card-body">{t('home.card4Body')}</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ========== METHODOLOGY ========== */}
                <MethodologyPipeline />

                {/* ========== CTA STRIP ========== */}
                <section className="cta-strip" id="cta-strip">
                    <div className="container">
                        <div className="cta-strip-eyebrow reveal">{t('home.ctaEyebrow')}</div>

                        <h2 className="cta-strip-headline reveal">
                            {renderT(t('home.ctaHeadline'))}
                        </h2>

                        <p className="cta-strip-sub reveal">
                            {t('home.ctaSub')}
                        </p>

                        <div className="cta-strip-actions reveal">
                            <Link to={lp('/contact#contact-form')} className="btn-primary">
                                {t('cta.startDiscovery')}
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </Link>
                            <Link to={lp('/manifesto')} className="btn-secondary">{t('cta.readManifesto')}</Link>
                        </div>

                        {/* Stat bar */}
                        <div className="cta-strip-stats">
                            <div className="cta-stat reveal">
                                <div className="cta-stat-value">{renderT(t('home.stat1Value'))}</div>
                                <div className="cta-stat-label">{t('home.stat1Label')}</div>
                            </div>
                            <div className="cta-stat-div reveal" />
                            <div className="cta-stat reveal reveal-d1">
                                <div className="cta-stat-value">{renderT(t('home.stat2Value'))}</div>
                                <div className="cta-stat-label">{t('home.stat2Label')}</div>
                            </div>
                            <div className="cta-stat-div reveal reveal-d1" />
                            <div className="cta-stat reveal reveal-d2">
                                <div className="cta-stat-value"><em>+</em>30</div>
                                <div className="cta-stat-label">{t('home.stat3Label')}</div>
                            </div>
                            <div className="cta-stat-div reveal reveal-d2" />
                            <div className="cta-stat reveal reveal-d3">
                                <div className="cta-stat-value">P<em>&amp;</em>L</div>
                                <div className="cta-stat-label">{t('home.stat4Label')}</div>
                            </div>
                        </div>
                    </div>
                </section>

            </div>{/* /.content-sections */}
        </main>
    );
};

export default Home;
